import { ReactNode, useState } from "react";
import {
  Branch,
  useListBranchQuery,
} from "@/services/backend/branch/api/branch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Button } from "@/shared/components/ui/button";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/shared/components/ui/command";
import { cn } from "@/shared/lib/utils";
import { CompanyName } from "@/services/backend/company/components/company-name";
import t from "@/lang/lang";
import { useShowCompanyQuery } from "../../company/api/company";
import { Filter } from "@/shared/lib/filter/filter";
import { UUID } from "@bitory-gmbh/nidavellir";

export function BranchesSelect({
  value,
  onValueChange,
  companyFilter = { active: false, values: [] },
  note,
}: {
  value?: string[];
  onValueChange: (value: string[]) => void;
  companyFilter?: Filter<UUID>;
  note?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const { data: list } = useListBranchQuery({
    company: companyFilter,
  });
  const selection = value ?? [];

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="justify-between"
        >
          {selection.length > 0 ? (
            selection
              .map((id) => list?.branches.find((c) => c.id === id)?.name)
              .join(", ")
          ) : (
            <span className="text-muted-foreground font-normal">
              {t("Niederlassungen auswählen ...")}
            </span>
          )}
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        {note && (
          <div className="text-muted-foreground border-b p-2 text-xs">
            {note}
          </div>
        )}
        <Command>
          <CommandInput placeholder={t("Suchen")} className="h-9" />
          <CommandEmpty>{t("Nichts gefunden")}</CommandEmpty>
          <CommandGroup>
            {list?.branches.map((branch) => (
              <BranchCommandItem
                key={branch.id}
                value={selection}
                onValueChange={(v) => {
                  onValueChange(v);
                  setOpen(false);
                }}
                branch={branch}
              />
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function BranchCommandItem({
  value,
  onValueChange,
  branch,
}: {
  value: string[];
  onValueChange: (v: string[]) => void;
  branch: Branch;
}) {
  const { data: company } = useShowCompanyQuery({ id: branch.companyId });

  if (!company) return null;

  return (
    <CommandItem
      key={branch.id}
      value={`${branch.name} ${company.shortName}`}
      onSelect={() => {
        if (value.some((cId) => cId === branch.id)) {
          onValueChange(value.filter((cId) => cId !== branch.id));
        } else {
          onValueChange([...value, branch.id]);
        }
      }}
    >
      <Check
        className={cn(
          "mr-2 h-4 w-4",
          value.some((cId) => cId === branch.id) ? "opacity-100" : "opacity-0",
        )}
      />
      <span>
        {branch.name} (
        <CompanyName companyId={branch.companyId} short />)
      </span>
    </CommandItem>
  );
}
